import React, { useMemo } from "react";
import { useGlobalContext } from "context/global";
import { ReactComponent as RefreshIcon } from "assets/svgs/refresh.svg";
import { useSelector } from "react-redux";
import PaymentInfo from "components/payment-info";
import SubscriptionProducts from "components/account/subscription/products";
import { ReactComponent as MealIcon } from "assets/svgs/meal.svg";
import formatAddress from "utils/format-address";
import Link from "helpers/link";
import clsx from "clsx/lite";
import ReactivateSubscription from "components/reactivate-subscription";
import { editSubscriptionProductsUrl } from "utils/constants";
import InlineNotice from "components/inline-notice";
import DiscountForm from "components/subscription-discounts/discount-form";
import DiscountCodes from "components/subscription-discounts/discount-codes";
import { ReactComponent as TagIcon } from "assets/svgs/tag.svg";

const SubscriptionOverview = ({ allProducts, data }) => {
	const { title, subtitle, mealSelectionSubtitle, isActive, showDiscountInput = true } = data || {};
	const { translation } = useGlobalContext();

	return (
		<div className="max-w-6xl mx-auto px-4 md:px-6 py-12">
			{title && <h1 className="text-3xl font-semibold text-center">{title}</h1>}
			{subtitle && <p className="text-center text-gray-500 mt-2">{subtitle}</p>}

			<div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-10">
				<SettingsCard />
				<MealsCard allProducts={allProducts} subtitle={mealSelectionSubtitle} />
			</div>
			{showDiscountInput &&
				<div className="bg-beige py-3.5 px-5 md:py-6 md:px-10 rounded-xl my-10 flex flex-col gap-3.5">
					<div className="flex items-center gap-3.5 min-h-[44px]">
						<TagIcon className="shrink-0" />
						<h3>{translation.subscription?.add_discount_code}</h3>
						<DiscountCodes />
					</div>

					<DiscountForm className={{
						form: "flex gap-3 md:gap-6 h-10 justify-between min-h-7 md:min-h-12",
						input: "border rounded px-3 md:px-4 text-sm w-full",
						button:
							"btn-primary py-2 md:py-0 rounded-3xl min-w-[40%] md:min-w-[30%] lg:min-w-[20%] normal-case tracking-normal"
					}} />
				</div>
			}
			{!isActive && (
				<div className="mt-10 text-center">
					<ReactivateSubscription
						className="!btn-orange py-3 px-7"
						buttonText={translation.subscription?.reactivate_and_curate_plan} />
					<p className="text-sm mt-2 w-3/5 mx-auto">{translation.reactivation?.no_charge_yet_customize}</p>
				</div>
			)}
		</div>
	);
};

const SettingsCard = () => {
	const { translation, cartSettings } = useGlobalContext();
	const { status, isActive, isCancelled, deliveryPolicy, shippingAddress, paymentMethodId, paymentMethods
	} = useSelector((state) => state.account.subscription);

	if (!shippingAddress || !deliveryPolicy) return null;

	const currentPayment = useMemo(() =>
		paymentMethods.find((method) => method.id === paymentMethodId), [paymentMethods, paymentMethodId]
	);

	const deliveryFrequencies = cartSettings.subscription.frequencies;

	const subTranslation = translation.subscription || {};

	const activeFrequency = deliveryFrequencies
		.find(freq => deliveryPolicy.intervalCount === freq.intervalCount && deliveryPolicy.interval === freq.interval);

	const settings = [
		{
			label: subTranslation?.status_title,
			value: subTranslation.status?.[status.toLowerCase()] || status,
			className:
				isActive ? "text-status-green" : isCancelled ? "text-red-600" : status === "PAUSED"
					? "text-orange-discount" : "text-gray-500"
		},
		{ label: subTranslation?.interval, value: activeFrequency?.name || "Unknown" },
		{ label: subTranslation?.address, value: formatAddress(shippingAddress) },
		{ label: subTranslation?.shipping_method, value: "Standard shipping (5,99€)" },
		{ label: subTranslation?.payment?.title, value: <PaymentInfo data={currentPayment} className="text-sm" /> }
	];

	return (
		<div className="subscription-overview-card">
			<div className="flex gap-2 items-center mb-6">
				<RefreshIcon className="size-6 shrink-0" />

				<p className="lg:text-h2-md text-body-xs font-heading">
					{translation.user.subscription_settings}
				</p>
			</div>

			<div className="flex flex-col gap-y-4 mx-5">
				{settings.map((setting, index) => (
					<div key={index}>
						<p className="text-sm font-semibold">{setting.label}</p>
						{typeof setting.value === "string" ?
							<p className={clsx("text-sm", setting.className)}>{setting.value}</p> :
							setting.value}
					</div>
				))}

				{isCancelled &&
					<InlineNotice message={translation.reactivation?.your_account_will_not_be_debited} />
				}
			</div>

			{isActive && (
				<Link to="/account/settings" className="btn-primary !py-2.5 mt-6 w-4/5 mx-auto">
					{translation.subscription?.manage_my_settings}
				</Link>
			)}
		</div>
	);
};

const MealsCard = ({ allProducts, subtitle }) => {
	const { translation } = useGlobalContext();

	const { isActive, totalWeight } = useSelector((state) => state.account.subscription);

	return (
		<div className="subscription-overview-card">
			<div className="flex gap-2 items-center mb-2">
				<MealIcon />
				<p className="lg:text-h2-md text-body-xs font-heading">{translation.shopping_flow?.your_selection} ({totalWeight})</p>
			</div>
			{subtitle && <p className="text-sm text-gray-500 mb-4">{subtitle}</p>}
			<SubscriptionProducts
				hideATCPrice className="!w-full !px-5 !mb-0" allProducts={allProducts}
				sliderProps={{ breakpoints: { 700: { slidesPerView: 2 } } }}
			/>

			{isActive && (
				<Link to={editSubscriptionProductsUrl} className="btn-primary !py-2.5 mt-6 w-4/5 mx-auto">
					{translation.subscription?.manage_my_selection}
				</Link>
			)}
		</div>
	);
};

export default SubscriptionOverview;